<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <candidates-tabs />

          <template v-if="job">
            <div
              class="d-flex flex-wrap align-items-start justify-content-between pt-3 pb-2"
            >
              <div class="mb-2">
                <h4 class="font-weight-bold mb-2">{{ job.title }}</h4>
                <job-status-badge :job="job" />
              </div>

              <div class="d-flex flex-wrap job-actions">
                <b-button
                  variant="outline-secondary"
                  class="bg-white text-dark back-btn"
                  :to="{ name: 'jobs.list-page' }"
                >
                  Back to Jobs
                </b-button>
                <b-button
                  v-if="canEdit"
                  variant="primary"
                  class="edit-btn"
                  :to="{ name: 'jobs.edit-page', params: { jobId: job.id } }"
                >
                  Edit
                </b-button>
                <b-button
                  v-if="canCopy"
                  variant="primary"
                  class="copy-btn"
                  :to="{ name: 'jobs.new-page', query: { copy_from: job.id } }"
                >
                  Copy as new job
                </b-button>
                <b-button
                  v-if="canPublish"
                  variant="primary"
                  class="publish-btn"
                  :disabled="isActing"
                  @click="onPublish"
                >
                  {{ job.status === 'draft' ? 'Publish' : 'Republish' }}
                </b-button>
                <b-button
                  v-if="canClose"
                  variant="outline-secondary"
                  class="close-btn"
                  :disabled="isActing"
                  @click="showCloseModal = true"
                >
                  Close job
                </b-button>
                <b-button
                  v-if="canDelete"
                  variant="danger"
                  class="delete-btn"
                  :disabled="isActing"
                  @click="showDeleteModal = true"
                >
                  Delete
                </b-button>
              </div>
            </div>

            <b-alert :show="isRemoved" variant="danger" class="removed-banner">
              <strong>This job was removed.</strong>
              <span v-if="job.removed_reason">
                Reason: {{ job.removed_reason }}
              </span>
            </b-alert>

            <div class="job-panel mb-4">
              <b-row class="job-facts">
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Employment type</div>
                  <div>{{ employmentTypeLabel(job.employment_type) }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Trade</div>
                  <div>{{ job.trade ? job.trade.name : '-' }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Location</div>
                  <div>{{ locationLabel(job) }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Pay</div>
                  <div>
                    {{ payLabel(job) }}
                    <small
                      v-if="job.pay && job.pay.type && !job.pay.show"
                      class="text-muted d-block"
                    >
                      Hidden from the advertisement
                    </small>
                  </div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Posted</div>
                  <div>{{ formatDate(job.published_at) }}</div>
                </b-col>
                <b-col v-if="job.live_from" md="4" class="mb-3">
                  <div class="fact-label">
                    {{ job.is_scheduled ? 'Goes live' : 'Live from' }}
                  </div>
                  <div>{{ formatDate(job.live_from) }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Expires</div>
                  <div>{{ formatDate(job.expires_at) }}</div>
                </b-col>
                <b-col v-if="job.created_by" md="4" class="mb-3">
                  <div class="fact-label">Created by</div>
                  <div>{{ job.created_by.full_name }}</div>
                </b-col>
                <b-col md="4" class="mb-3">
                  <div class="fact-label">Visible to</div>
                  <div>{{ visibilityLabel }}</div>
                </b-col>
                <b-col v-if="job.project" md="4" class="mb-3">
                  <div class="fact-label">Project</div>
                  <b-link
                    :to="{
                      name: 'projects.view-page',
                      params: { id: job.project.id },
                    }"
                  >
                    {{ job.project.name }}
                  </b-link>
                </b-col>
                <template v-if="job.is_fifo">
                  <b-col md="4" class="mb-3">
                    <div class="fact-label">FIFO point</div>
                    <div>{{ job.fifo_point_location || '-' }}</div>
                  </b-col>
                  <b-col md="4" class="mb-3">
                    <div class="fact-label">Site</div>
                    <div>{{ job.site_location || '-' }}</div>
                  </b-col>
                </template>
              </b-row>

              <job-listing-details :job="job" />

              <div class="fact-label">Description</div>
              <!-- Plain text interpolation (never v-html); pre-wrap keeps line breaks. -->
              <p class="job-description mb-0">{{ job.description }}</p>
            </div>

            <job-insights :tiles="insightTiles" />

            <div class="mb-5">
              <job-applicants
                :applications="applications"
                :loading="isLoadingApplications"
                :can-manage="canManageApplicants"
                :job-closed="jobClosed"
                :can-view-profiles="canViewProfiles"
                :status="applicantStatus"
                :search="applicantSearch"
                :page="applicantPage"
                :per-page="applicantPerPage"
                :total="applicantTotal"
                :updating-id="updatingApplicationId"
                :resume-loading-id="resumeLoadingId"
                @view-resume="onViewResume"
                @view-profile="onViewProfile"
                @invite="onInvite"
                @filter="onApplicantFilter"
                @search="onApplicantSearch"
                @page-change="onApplicantPage"
                @clear="onApplicantClear"
                @update-status="onApplicationStatus"
                @reject="onReject"
                @reinstate="onReinstate"
              />
            </div>

            <div class="job-panel mb-5">
              <div class="d-flex align-items-center justify-content-between">
                <h5 class="font-weight-bold mb-0">History</h5>
                <b-button
                  variant="outline-secondary"
                  class="bg-white text-dark history-toggle"
                  @click="toggleHistory"
                >
                  {{ showHistory ? 'Hide' : 'Show' }}
                </b-button>
              </div>
              <template v-if="showHistory">
                <p v-if="isLoadingHistory" class="text-muted mt-3 mb-0">
                  Loading history...
                </p>
                <p v-else-if="!history.length" class="text-muted mt-3 mb-0">
                  Nothing recorded yet.
                </p>
                <ul v-else class="history-list mt-3 mb-0">
                  <li v-for="entry in history" :key="entry.id">
                    <span class="history-date">{{
                      formatDateTime(entry.created_at)
                    }}</span>
                    {{ entry.description }}
                  </li>
                </ul>
              </template>
            </div>
          </template>
        </b-col>
      </b-row>
    </b-container>

    <workforce-invitation-modal
      :show-modal="showInviteModal"
      :candidate="inviteCandidate"
      @closeModal="showInviteModal = false"
      @sent="onInviteSent"
    />
    <confirmation-modal
      :show-modal="showCloseModal"
      title="Close Job"
      message="Closing stops new applications. You can republish it later."
      ok-label="YES, CLOSE JOB"
      @close="onCloseModalCancel"
      @ok="onClose"
    >
      <b-form-checkbox v-model="notifyUnsuccessful" class="notify-unsuccessful">
        Send a notification to all unsuccessful applicants. If left unticked
        they are not notified, but will see "not successful, job closed" on
        their application.
      </b-form-checkbox>
    </confirmation-modal>
    <confirmation-modal
      :show-modal="showDeleteModal"
      title="Job Deletion"
      message="Are you sure you want to delete this job? This cannot be undone."
      ok-label="YES, DELETE JOB"
      ok-variant="danger"
      @close="showDeleteModal = false"
      @ok="onDelete"
    />
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CandidatesTabs from '@/components/candidates/CandidatesTabs.vue'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import JobApplicants from '@/components/jobPostings/JobApplicants.vue'
import JobInsights from '@/components/jobPostings/JobInsights.vue'
import JobListingDetails from '@/components/jobPostings/JobListingDetails.vue'
import JobStatusBadge from '@/components/jobPostings/JobStatusBadge.vue'
import WorkforceInvitationModal from '@/components/modals/WorkforceInvitationModal.vue'
import apiToasts from '@/mixins/apiToasts'
import {
  APPLICATION_STATUS_LABELS,
  VISIBILITY_OPTIONS,
  employmentTypeLabel,
  formatDate,
  locationLabel,
  payLabel,
} from '@/utils/jobPostings'
import { parseServerDate } from '@/utils/dateHelper'

export default {
  name: 'JobsViewJob',

  components: {
    CandidatesTabs,
    ConfirmationModal,
    JobApplicants,
    JobInsights,
    JobListingDetails,
    JobStatusBadge,
    WorkforceInvitationModal,
  },

  mixins: [apiToasts],

  data() {
    return {
      isLoading: false,
      isLoadingApplications: false,
      isActing: false,
      updatingApplicationId: null,
      resumeLoadingId: null,
      showInviteModal: false,
      inviteCandidate: null,
      applicantStatus: '',
      applicantSearch: '',
      applicantPage: 1,
      applicantPerPage: 20,
      showCloseModal: false,
      notifyUnsuccessful: false,
      showDeleteModal: false,
      showHistory: false,
      isLoadingHistory: false,
      history: [],
    }
  },

  computed: {
    ...mapGetters('jobPosting', {
      job: 'currentJob',
      applications: 'applications',
      applicationsMeta: 'applicationsMeta',
      insights: 'jobInsights',
    }),
    ...mapGetters('auth', ['hasPermission']),

    // The candidate profile (and workforce invites) are part of Open to Work,
    // so they follow its flag and the candidates permission.
    canViewProfiles() {
      return (
        !!(this.$env && this.$env.openToWorkEnabled) &&
        this.hasPermission('candidates.profile.view')
      )
    },

    jobClosed() {
      return (
        !!this.job &&
        (this.job.status === 'closed' ||
          (this.job.status === 'published' && !!this.job.is_expired))
      )
    },

    // Count across every page for the current filters (from the API).
    applicantTotal() {
      const meta = this.applicationsMeta
      return meta && meta.total ? Number(meta.total) : 0
    },

    // A draft has never been seen by tradies, so its numbers would all be
    // zeros; show nothing until it is published.
    insightTiles() {
      const i = this.insights
      if (!i || !this.job || this.job.status === 'draft') return []
      const tiles = [
        {
          key: 'total',
          label: 'Total Applicants',
          value: i.total,
          icon: 'people-fill',
        },
        {
          key: 'new',
          label: 'New to Review',
          value: i.new,
          icon: 'person-plus-fill',
        },
        {
          key: 'shortlisted',
          label: 'Shortlisted',
          value: i.shortlisted,
          icon: 'star-fill',
        },
        {
          key: 'rejected',
          label: 'Rejected',
          value: i.rejected,
          icon: 'person-x-fill',
        },
        {
          key: 'views',
          label: 'Views',
          value: i.views,
          icon: 'eye-fill',
        },
        {
          key: 'withdrawn',
          label: 'Withdrawn',
          value: i.withdrawn,
          icon: 'box-arrow-left',
        },
        {
          key: 'live',
          label: 'Days Live',
          value: i.days_live === null ? '-' : i.days_live,
          icon: 'broadcast',
        },
      ]
      if (i.days_left !== null && i.days_left !== undefined) {
        tiles.push({
          key: 'left',
          label: 'Days Left',
          value: Math.max(i.days_left, 0),
          icon: 'hourglass-split',
        })
      }
      return tiles
    },

    visibilityLabel() {
      const hit = VISIBILITY_OPTIONS.find(
        (o) => o.value === (this.job && this.job.visibility),
      )
      return hit ? hit.text : '-'
    },

    // Raw API status drives the actions (the badge renders is_expired itself).
    isRemoved() {
      return !!this.job && this.job.status === 'removed'
    },
    // A closed job is a record of what was advertised; changing it needs a copy.
    canEdit() {
      return (
        !!this.job &&
        !this.isRemoved &&
        !this.jobClosed &&
        this.hasPermission('jobs.edit')
      )
    },
    canManageApplicants() {
      return !!this.job && !this.isRemoved && this.hasPermission('jobs.edit')
    },
    canCopy() {
      return (
        this.jobClosed && !this.isRemoved && this.hasPermission('jobs.create')
      )
    },
    canPublish() {
      return (
        !!this.job &&
        (['draft', 'closed'].includes(this.job.status) ||
          // Published but past its expiry: can be republished for another 30 days.
          (this.job.status === 'published' && this.job.is_expired)) &&
        this.hasPermission('jobs.publish')
      )
    },
    canClose() {
      return (
        !!this.job &&
        this.job.status === 'published' &&
        !this.job.is_expired &&
        this.hasPermission('jobs.publish')
      )
    },
    canDelete() {
      return !!this.job && this.hasPermission('jobs.delete')
    },
  },

  async created() {
    this.isLoading = true
    try {
      await this.fetchJob(this.$route.params.jobId)
    } catch (err) {
      this.onError(err, 'Could not load this job.')
      this.$router.replace({ name: 'jobs.list-page' })
      return
    } finally {
      this.isLoading = false
    }
    this.loadApplications()
    // A nice-to-have; a failure must not break the page.
    this.fetchJobInsights(this.$route.params.jobId).catch(() => {})
  },

  methods: {
    ...mapActions('jobPosting', [
      'fetchJob',
      'publishJob',
      'closeJob',
      'deleteJob',
      'fetchApplications',
      'updateApplicationStatus',
      'fetchJobInsights',
      'fetchApplicantResume',
      'fetchHistory',
      'markNotSuited',
      'reinstateApplication',
    ]),

    formatDateTime(value) {
      const d = parseServerDate(value)
      if (!d) return '-'
      return d.toLocaleString('en-AU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    async toggleHistory() {
      this.showHistory = !this.showHistory
      if (this.showHistory) await this.loadHistory()
    },

    async loadHistory() {
      this.isLoadingHistory = true
      try {
        this.history = await this.fetchHistory(this.$route.params.jobId)
      } catch (err) {
        this.onError(err, 'Could not load the history.')
      } finally {
        this.isLoadingHistory = false
      }
    },

    // Hide this job posting from this tradie. It can be undone from the Not suited
    // tab on the Jobs list.
    async onNotSuited(application) {
      this.updatingApplicationId = application.id
      try {
        await this.markNotSuited({
          jobId: this.$route.params.jobId,
          applicationId: application.id,
        })
        if (this.applicantStatus) await this.loadApplications()
        this.fetchJobInsights(this.$route.params.jobId).catch(() => {})
        if (this.showHistory) this.loadHistory()
        this.showToast(
          'Success',
          `${application.applicant.name} has been marked as not suited and will no longer see this job posting.`,
        )
      } catch (err) {
        this.onError(err, 'Could not mark this applicant as not suited.')
      } finally {
        this.updatingApplicationId = null
      }
    },

    employmentTypeLabel,
    formatDate,
    locationLabel,
    payLabel,

    async loadApplications() {
      this.isLoadingApplications = true
      try {
        await this.fetchApplications({
          id: this.$route.params.jobId,
          status: this.applicantStatus,
          q: this.applicantSearch,
          page: this.applicantPage,
          perPage: this.applicantPerPage,
        })
      } catch (err) {
        this.onError(err, 'Could not load applicants.')
      } finally {
        this.isLoadingApplications = false
      }
    },

    // Any change to the filters starts again from page 1.
    onApplicantFilter(status) {
      this.applicantStatus = status
      this.applicantPage = 1
      this.loadApplications()
    },

    onApplicantSearch(q) {
      this.applicantSearch = q
      this.applicantPage = 1
      this.loadApplications()
    },

    onApplicantPage(page) {
      this.applicantPage = page
      this.loadApplications()
    },

    onApplicantClear() {
      this.applicantStatus = ''
      this.applicantSearch = ''
      this.applicantPage = 1
      this.loadApplications()
    },

    onViewProfile(application) {
      this.$router.push({
        name: 'candidates.view-candidate',
        params: { candidateId: application.applicant.id },
      })
    },

    onInvite(application) {
      this.inviteCandidate = {
        id: application.applicant.id,
        name: application.applicant.name,
        workforce_invitation_status: application.workforce_invitation_status,
      }
      this.showInviteModal = true
    },

    // Reload so the card flips to "Invitation Sent".
    async onInviteSent() {
      this.showInviteModal = false
      this.showToast('Success', 'Workforce invitation sent.')
      await this.loadApplications()
    },

    // The signed URL comes back after a request, so open the tab first (inside
    // the click) or the popup blocker eats it, then point it at the file.
    async onViewResume(application) {
      const tab = window.open('', '_blank')
      this.resumeLoadingId = application.id
      try {
        const url = await this.fetchApplicantResume({
          jobId: this.$route.params.jobId,
          applicationId: application.id,
        })
        if (!url) throw new Error('No resume available.')
        if (tab) tab.location.href = url
        else window.location.href = url
      } catch (err) {
        if (tab) tab.close()
        this.onError(err, 'Could not open this resume.')
      } finally {
        this.resumeLoadingId = null
      }
    },

    // Reject, optionally also hiding this job from the tradie. One after the
    // other so the two refreshes don't race.
    async onReject(application, alsoNotSuited) {
      await this.onApplicationStatus(application, 'rejected')
      if (alsoNotSuited) await this.onNotSuited(application)
    },

    async onReinstate(application) {
      this.updatingApplicationId = application.id
      try {
        await this.reinstateApplication({
          jobId: this.$route.params.jobId,
          applicationId: application.id,
        })
        if (this.applicantStatus) await this.loadApplications()
        this.fetchJobInsights(this.$route.params.jobId).catch(() => {})
        if (this.showHistory) this.loadHistory()
        this.showToast(
          'Success',
          `${application.applicant.name} has been reinstated. Their application can be reviewed again.`,
        )
      } catch (err) {
        this.onError(err, 'Could not reinstate this applicant.')
      } finally {
        this.updatingApplicationId = null
      }
    },

    async onApplicationStatus(application, status, requestContact = false) {
      this.updatingApplicationId = application.id
      try {
        await this.updateApplicationStatus({
          jobId: this.$route.params.jobId,
          applicationId: application.id,
          status,
          requestContact,
        })
        // A filtered view may no longer include the row once its status moves.
        if (this.applicantStatus) await this.loadApplications()
        this.fetchJobInsights(this.$route.params.jobId).catch(() => {})
        if (this.showHistory) this.loadHistory()
        const label = APPLICATION_STATUS_LABELS[status].toLowerCase()
        this.showToast(
          'Success',
          requestContact
            ? 'Applicant shortlisted. They have been asked to share their contact details.'
            : `Applicant marked as ${label}.`,
        )
      } catch (err) {
        this.onError(err, 'Could not update the applicant.')
      } finally {
        this.updatingApplicationId = null
      }
    },

    async onPublish() {
      await this.runAction(() => this.publishJob(this.job.id), 'Job published.')
    },

    onCloseModalCancel() {
      this.showCloseModal = false
      this.notifyUnsuccessful = false
    },

    async onClose() {
      const notifyUnsuccessful = this.notifyUnsuccessful
      this.notifyUnsuccessful = false
      await this.runAction(
        () => this.closeJob({ id: this.job.id, notifyUnsuccessful }),
        'Job closed.',
      )
    },

    async onDelete() {
      this.isActing = true
      try {
        await this.deleteJob(this.job.id)
        this.showToast('Success', 'Job deleted.')
        this.$router.push({ name: 'jobs.list-page' })
      } catch (err) {
        this.onError(err, 'Could not delete this job.')
      } finally {
        this.isActing = false
      }
    },

    async runAction(action, successMessage) {
      this.isActing = true
      try {
        await action()
        // Publish / close changes the days-live and days-left numbers.
        this.fetchJobInsights(this.$route.params.jobId).catch(() => {})
        if (this.showHistory) this.loadHistory()
        this.showToast('Success', successMessage)
      } catch (err) {
        this.onError(err, 'Something went wrong.')
      } finally {
        this.isActing = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Gray values follow the newer Tailwind-style scale in DESIGN.md.
.job-actions {
  gap: 12px;

  // Override the global 56px-tall .btn: a fixed, centred height keeps the Edit
  // link (an <a>) and the real buttons aligned and a sensible size.
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    min-width: 110px;
    padding: 0 20px;
    font-weight: 600;
  }
}

.job-panel {
  background: #fff;
  border-radius: 10px;
  padding: 1.5rem;
  box-shadow: 0px 10px 25px #00429d0d;
}

.fact-label {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.job-description {
  color: #111827;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.history-toggle {
  height: 36px;
  min-width: 80px;
  padding: 0 16px;
  font-weight: 600;
}

.history-list {
  list-style: none;
  padding: 0;
  color: #374151;

  li {
    padding: 0.5rem 0;
    border-bottom: 1px solid #f3f4f6;
  }
}

.history-date {
  display: inline-block;
  min-width: 140px;
  margin-right: 0.5rem;
  font-size: 0.75rem;
  color: #9ca3af;
}
</style>
